import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CreditCard, Check, X, Delete, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* EFTPOS CEF — /cef/eftpos/:serverId?store=gas:docks&amount=240
   The legal mirror of the robbery: a card terminal the player actually operates.
   Swipe/drag the card across the reader → under $100 taps through; over the
   contactless limit the machine demands the CivicOS phone PIN. Three wrong PINs
   freeze the card for 30 minutes (server-side). */

export default function EftposCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const storeId = params.get("store") || "gas:docks";
  const amount = parseFloat(params.get("amount") || "240");
  const storeName = params.get("name") || "24/7 Fuel Dock";

  const [stage, setStage] = useState("idle"); // idle | swiping | pin | processing | approved | declined
  const [pin, setPin] = useState("");
  const [swipe, setSwipe] = useState(0); // 0..1 drag progress
  const [receipt, setReceipt] = useState("");
  const [err, setErr] = useState("");
  const dragging = useRef(false);
  const needsPin = amount > 100;

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const pay = useCallback(async (pinCode) => {
    setStage("processing");
    if (demo) {
      setTimeout(() => {
        if (needsPin && pinCode !== "4842") { setStage("pin"); setPin(""); setErr("WRONG PIN (demo pin is 4842)"); return; }
        setReceipt(`NEW CITIZEN BANK · ${storeName} · $${amount.toFixed(2)} · APPROVED (preview)`);
        setStage("approved");
      }, 1400);
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/eftpos/pay`, { store_id: storeId, amount, pin: pinCode }, { params: { token } });
      setReceipt(r.data.receipt);
      setStage("approved");
    } catch (e) {
      const d = e.response?.data?.detail || "DECLINED";
      setErr(d);
      if (String(d).includes("WRONG PIN")) { setStage("pin"); setPin(""); }
      else setStage("declined");
    }
  }, [demo, needsPin, pin, serverId, storeId, amount, storeName, token]);

  // card swipe — pointer drag across the reader slot
  const onDragStart = (e) => { dragging.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onDragMove = (e) => {
    if (!dragging.current || stage !== "swiping") return;
    const track = document.getElementById("eftpos-track");
    if (!track) return;
    const r = track.getBoundingClientRect();
    setSwipe(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
  };
  const onDragEnd = () => {
    dragging.current = false;
    if (stage !== "swiping") return;
    if (swipe > 0.85) {
      if (needsPin) setStage("pin");
      else pay("");
    } else setSwipe(0);
  };

  const pinKey = (k) => {
    if (k === "del") { setPin((p) => p.slice(0, -1)); setErr(""); return; }
    if (pin.length >= 6) return;
    const next = pin + k;
    setPin(next);
    setErr("");
    if (next.length >= 4) setTimeout(() => pay(next), 250);
  };

  const dots = "●".repeat(pin.length) + "○".repeat(Math.max(0, 4 - pin.length));

  return (
    <div data-testid="eftpos-page" className="min-h-screen bg-transparent relative overflow-hidden flex items-end justify-center pb-10">
      <style>{`
        @keyframes ncFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @keyframes eftApproved { 0% { box-shadow: 0 0 0 rgba(74,222,128,0); } 50% { box-shadow: 0 0 60px rgba(74,222,128,0.5); } 100% { box-shadow: 0 0 20px rgba(74,222,128,0.2); } }
      `}</style>
      {demo && <PreviewRibbon what="eftpos terminal" />}

      <div className="w-80 border border-zinc-700 bg-gradient-to-b from-zinc-800 to-zinc-900 rounded-2xl p-5 shadow-[0_30px_80px_rgba(0,0,0,0.85)]"
        style={{ animation: stage === "approved" ? "eftApproved 1.2s ease both" : "ncFadeUp 0.3s ease both" }}>
        {/* terminal header */}
        <div className="flex items-center justify-between">
          <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">NC Bank · EFTPOS</p>
          <CreditCard className="w-4 h-4 text-zinc-500" />
        </div>
        {/* screen */}
        <div data-testid="eftpos-screen" className="mt-3 rounded-lg bg-[#04140a] border border-emerald-900/50 px-4 py-3.5 font-mono min-h-20">
          {stage === "idle" && <p className="text-emerald-400 text-[11px]">WELCOME<br /><span className="text-emerald-700 text-[9px]">{storeName}</span></p>}
          {stage === "swiping" && <p className="text-emerald-400 text-[11px]">TOTAL ${amount.toFixed(2)}<br /><span className="text-emerald-700 text-[9px]">{needsPin ? "SWIPE CARD — PIN WILL BE REQUIRED" : "SWIPE CARD TO PAY"}</span></p>}
          {stage === "pin" && <p className="text-emerald-400 text-[11px]">ENTER PIN<br /><span data-testid="eftpos-pin-dots" className="text-lg tracking-[0.4em]">{dots}</span>{err && <span data-testid="eftpos-error" className="block text-red-400 text-[8px] mt-1">{err}</span>}</p>}
          {stage === "processing" && <p className="text-emerald-400 text-[11px] animate-pulse">PROCESSING…<br /><span className="text-emerald-700 text-[9px]">CONTACTING BANK</span></p>}
          {stage === "approved" && <p data-testid="eftpos-approved" className="text-emerald-300 text-[11px]">APPROVED ✓<br /><span className="text-emerald-700 text-[8px]">{receipt}</span></p>}
          {stage === "declined" && <p data-testid="eftpos-declined" className="text-red-400 text-[11px]">DECLINED<br /><span className="text-red-800 text-[8px]">{err}</span></p>}
        </div>

        {/* idle → start */}
        {stage === "idle" && (
          <button data-testid="eftpos-begin" onClick={() => setStage("swiping")}
            className="mt-4 w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-black transition-colors">
            Charge ${amount.toFixed(2)}
          </button>
        )}

        {/* swipe track + card */}
        {stage === "swiping" && (
          <div className="mt-4 select-none">
            <div id="eftpos-track" className="relative h-14 rounded-lg bg-black/60 border border-zinc-700 overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-emerald-500/20 transition-none" style={{ width: `${swipe * 100}%` }} />
              <p className="absolute inset-0 flex items-center justify-center font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500 pointer-events-none">
                {swipe > 0.85 ? "Release to pay" : "Drag card through →"}
              </p>
              <div data-testid="eftpos-card" onPointerDown={onDragStart} onPointerMove={onDragMove} onPointerUp={onDragEnd}
                className="absolute top-1.5 h-11 w-16 rounded-md bg-gradient-to-br from-sky-500 to-indigo-700 border border-white/30 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none"
                style={{ left: `calc(${swipe * 100}% - ${swipe * 4}rem + 0.25rem)` }}>
                <CreditCard className="w-5 h-5 text-white/90" />
              </div>
            </div>
            <p className="font-mono text-[7px] text-zinc-600 uppercase tracking-[0.2em] mt-2 text-center">card never leaves your hand · chip fails 1 in 30 like real life</p>
          </div>
        )}

        {/* PIN pad */}
        {stage === "pin" && (
          <div className="mt-4 grid grid-cols-3 gap-1.5">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "del", "0", "ok"].map((k) => (
              <button key={k} data-testid={`eftpos-key-${k}`} onClick={() => (k === "ok" ? pin.length >= 4 && pay(pin) : pinKey(k))}
                className="py-2.5 rounded-md bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 font-mono text-sm text-zinc-100 transition-colors flex items-center justify-center">
                {k === "del" ? <Delete className="w-4 h-4" /> : k === "ok" ? <Check className="w-4 h-4 text-emerald-400" /> : k}
              </button>
            ))}
          </div>
        )}

        {(stage === "approved" || stage === "declined") && (
          <button data-testid="eftpos-done" onClick={() => { setStage("idle"); setPin(""); setSwipe(0); setErr(""); setReceipt(""); }}
            className="mt-4 w-full py-2.5 rounded-lg border border-zinc-600 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-300 hover:bg-zinc-800 transition-colors flex items-center justify-center gap-1.5">
            {stage === "approved" ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} Done
          </button>
        )}

        <p className="mt-3 font-mono text-[7px] text-zinc-600 uppercase tracking-[0.2em] flex items-center gap-1 justify-center">
          <Lock className="w-2.5 h-2.5" /> PIN is your CivicOS lock · 3 wrong freezes the card 30 min
        </p>
      </div>
    </div>
  );
}
